/**
 * The sync card's pinned slot, directly under the navbar in the top-right corner.
 *
 * It deliberately does NOT live in the toast stack. As a toast it was a peer of every
 * unrelated notification: arriving toasts pushed it down, expiring ones pulled it back up,
 * and its position depended on who else happened to be on screen. A sync is a single
 * ongoing operation, not an event — it gets a fixed place, and the toast stack is offset
 * below it via a CSS variable so the two never overlap.
 */

import React, { useLayoutEffect, useRef } from 'react';
import { useSyncProgress } from '../contexts/SyncProgressContext';
import { useOperationalSettings } from '../contexts/OperationalSettingsContext';
import { SyncProgressCard } from './SyncProgressCard';

/** Read by the Toaster in App.tsx as `top: var(--panther-toast-top, 70px)`. */
export const TOAST_TOP_VAR = '--panther-toast-top';

/** Navbar height + margin; matches the Toaster's default offset. */
const SLOT_TOP_PX = 70;
const GAP_PX = 8;

export function SyncProgressCardSlot() {
  const { data, isExpanded, expand, close } = useSyncProgress();
  const { settings } = useOperationalSettings();
  const ref = useRef<HTMLDivElement>(null);

  const visible = !!data && !isExpanded;

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.removeProperty(TOAST_TOP_VAR);
      return;
    }
    const height = ref.current?.offsetHeight ?? 0;
    root.style.setProperty(TOAST_TOP_VAR, `${SLOT_TOP_PX + height + GAP_PX}px`);
    return () => {
      root.style.removeProperty(TOAST_TOP_VAR);
    };
  }, [visible, data]);

  if (!visible) return null;

  return (
    <div
      ref={ref}
      data-testid="sync-card-slot"
      className="fixed right-4 z-[150] animate-in fade-in slide-in-from-top-2 duration-200"
      style={{ top: SLOT_TOP_PX }}
    >
      <SyncProgressCard
        data={data!}
        isDark={settings.theme === 'dark'}
        onExpand={expand}
        onClose={close}
      />
    </div>
  );
}
